// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { buildCaseAddressLegendAnalysis, normalizeCaseAddressLegendAddressKey } from './CaseBuddyAddressLegend.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { getCaseSuspiciousAddressLegendEntries, normalizeCaseSuspiciousHash, parseCaseSuspiciousAmount } from './CaseBuddySuspiciousAddressTable.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_DORMANT_ADDRESS_TABLE_HEADERS = Object.freeze(['Address', 'Funds Followed']);

export const CASE_DORMANT_ADDRESS_MONITORING_HEADERS = Object.freeze(['Address', 'Amount and Token', 'Funds Followed', 'Related Txn Hash']);

export function normalizeCaseDormantAddressHash(value = '') {
  return normalizeCaseSuspiciousHash(value);
}

export function parseCaseDormantAmount(value = '') {
  return parseCaseSuspiciousAmount(value);
}

export function truncateCaseDormantAmount(amount = 0, token = '') {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return null;
  const normalizedToken = normalizeCell(token).toUpperCase();
  const decimals = normalizedToken === 'USDT' || normalizedToken === 'USDC' ? 2 : 4;
  const factor = 10 ** decimals;
  return Math.trunc(numeric * factor) / factor;
}

export function formatCaseDormantAmount(amount = 0, token = '') {
  const truncated = truncateCaseDormantAmount(amount, token);
  if (truncated == null) return '';
  const normalizedToken = normalizeCell(token).toUpperCase();
  const decimals = normalizedToken === 'USDT' || normalizedToken === 'USDC' ? 2 : 4;
  return truncated.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatCaseDormantFundsString(raw = '') {
  const text = normalizeCell(raw);
  if (!text) return '';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return text;
  const token = parts[1];
  const amount = parseCaseDormantAmount(parts[0]);
  if (amount == null) return text;
  return `${formatCaseDormantAmount(amount, token)} ${token}`.trim();
}

export function buildCaseDormantAmountAndToken(rawAmount = '', rawToken = '') {
  const token = normalizeCell(rawToken);
  if (!token) return '';
  const amount = parseCaseDormantAmount(rawAmount);
  if (amount == null) return '';
  return `${formatCaseDormantAmount(amount, token)} ${token}`.trim();
}

export function getCaseDormantLegendEntries(legendAnalysis = {}) {
  return getCaseSuspiciousAddressLegendEntries(legendAnalysis);
}

export function buildCaseDormantLegendLookup(legendAnalysis = {}) {
  const lookup = new Map();
  getCaseDormantLegendEntries(legendAnalysis).forEach((entry) => {
    const address = normalizeCell(entry?.address || entry?.Address || entry?.preferredDisplayAddress || entry?.PreferredDisplayAddress);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (!key || lookup.has(key)) return;
    const label = normalizeCell(entry?.label || entry?.Label || entry?.proposedLegendLabel || entry?.ProposedLegendLabel);
    const labelFamily = normalizeCell(entry?.labelFamily || entry?.family || entry?.LabelFamily || entry?.label_family);
    const entityPresentedName = normalizeCell(entry?.entityPresentedName || entry?.entity_presented_name || entry?.EntityPresentedName || entry?.entityName);
    lookup.set(key, {
      normalizedAddress: key,
      address,
      label,
      labelFamily,
      entityPresentedName
    });
  });
  return lookup;
}

export function caseDormantNotesIndicateDormant(notes = '') {
  const lower = normalizeCell(notes).toLowerCase();
  if (!lower) return false;
  return lower.includes('a portion of') ||
    lower.includes('funds remain at this address') ||
    lower.includes('funds remains at this address') ||
    lower.includes('remains at this address') ||
    lower.includes('dormant address') ||
    lower.includes('dormant wallet');
}

export function caseDormantLegendIndicatesDormant(legend = null) {
  if (!legend) return false;
  const family = normalizeCell(legend.labelFamily).toLowerCase();
  const label = normalizeCell(legend.label);
  return family === 'dormant' || /^dormant\b/i.test(label) || /\bdormant\s+(?:address|wallet)\b/i.test(label);
}

export function caseDormantLegendClearlyIdentifiesNonDormant(legend = null) {
  if (!legend) return false;
  const label = normalizeCell(legend.label);
  const family = normalizeCell(legend.labelFamily).toLowerCase();
  const entity = normalizeCell(legend.entityPresentedName);
  if (/^victim\b/i.test(label)) return true;
  if (/deposit\s+address/i.test(label) || /hot\s+wallet(?:\s+address)?/i.test(label)) return true;
  if (family && family !== 'dormant' && (family.startsWith('exchange') || family === 'non_exchange' || family === 'victim')) return true;
  if (entity && family !== 'dormant' && !/^suspect$/i.test(family)) return true;
  return false;
}

export function buildCaseDormantTableSummary(state = {}) {
  if (!state.legendBuildIsCurrent) return 'Dormant address deliverables need the Address Legend to be built from current Main Data first.';
  if (!state.legendReviewIsCurrent) return 'Dormant address deliverables need the Address Legend to be reviewed for the current case data first.';
  const official = Number(state.officialRowCount) || 0;
  const monitoring = Number(state.monitoringRowCount) || 0;
  const txCount = Number(state.uniqueTransactionCount) || 0;
  let summary = `Dormant address deliverables are buildable with ${official} clean dormant address row${official === 1 ? '' : 's'} and ${monitoring} monitoring row${monitoring === 1 ? '' : 's'} from ${txCount} qualifying unique transaction${txCount === 1 ? '' : 's'}.`;
  if ((state.excludedIdentifiedAddressCount || 0) > 0) {
    summary += ` ${state.excludedIdentifiedAddressCount} identified address${state.excludedIdentifiedAddressCount === 1 ? ' was' : 'es were'} excluded because the reviewed legend identifies it as a victim, exchange/service, deposit address, hot wallet, or other non-dormant entity.`;
  }
  return summary;
}

export function addCaseDormantExcludedExample(state = {}, label = '') {
  const text = normalizeCell(label);
  if (!text) return;
  if (!Array.isArray(state.excludedExamples)) state.excludedExamples = [];
  if (!state.excludedExamples.some(existing => normalizeCell(existing).toLowerCase() === text.toLowerCase())) {
    state.excludedExamples.push(text);
  }
}

export function buildCaseDormantAddressTableRowsFromOccurrences(occurrences = []) {
  const groups = new Map();
  (Array.isArray(occurrences) ? occurrences : []).forEach((occurrence) => {
    const key = normalizeCaseAddressLegendAddressKey(occurrence?.address);
    if (!key) return;
    if (!groups.has(key)) {
      groups.set(key, {
        normalizedAddress: key,
        address: normalizeCell(occurrence.address),
        transactionHashes: new Set(),
        fundsFollowedDisplay: [],
        fundsFollowedCsv: [],
        occurrences: []
      });
    }
    const group = groups.get(key);
    group.transactionHashes.add(normalizeCaseDormantAddressHash(occurrence.relatedTransactionHash));
    group.fundsFollowedDisplay.push(normalizeCell(occurrence.fundsFollowed));
    group.fundsFollowedCsv.push(normalizeCell(occurrence.fundsFollowed));
    group.occurrences.push(occurrence);
  });

  return Array.from(groups.values()).map((group) => {
    const fundsFollowedDisplay = group.fundsFollowedDisplay.filter(Boolean).join('\n');
    const fundsFollowedCell = group.fundsFollowedDisplay.filter(Boolean).join('\n');
    const fundsFollowedCsv = group.fundsFollowedCsv.filter(Boolean).join('\n');
    return {
      key: `dormant-address-${caseBuddyHashString(`${group.normalizedAddress}|${fundsFollowedDisplay}`)}`,
      normalizedAddress: group.normalizedAddress,
      address: group.address,
      fundsFollowedDisplay,
      fundsFollowedCell,
      fundsFollowedCsv,
      uniqueTransactionCount: group.transactionHashes.size,
      occurrenceCount: group.occurrences.length,
      sourceRows: group.occurrences.slice(),
      searchText: [group.address, fundsFollowedDisplay, ...group.occurrences.flatMap(row => [row.relatedTransactionHash, row.amountAndToken, row.sheetRowNumber])]
        .map(value => normalizeCell(value).toLowerCase())
        .filter(Boolean)
        .join(' ')
    };
  }).sort((left, right) => normalizeCell(left.address).localeCompare(normalizeCell(right.address), undefined, { numeric: true, sensitivity: 'base' }));
}

export function buildCaseDormantAddressDeliverablesState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(
    rows,
    effectiveCurator,
    workflowState,
    { labelDormantAddressesSeparately: !!options.labelDormantAddressesSeparately }
  );
  const legendLookup = buildCaseDormantLegendLookup(legendAnalysis);
  const entries = parseCaseEntries(rows);
  const seenOccurrenceKeys = new Set();
  const uniqueTransactionHashes = new Set();
  const occurrences = [];
  const state = {
    caseId: normalizeCell(options.caseId),
    legendBuildIsCurrent: !!legendAnalysis.buildIsCurrent,
    legendReviewIsCurrent: !!legendAnalysis.reviewIsCurrent,
    tableIsCurrent: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    canBuild: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    legendEntryCount: legendLookup.size,
    uniqueTransactionCount: 0,
    officialRowCount: 0,
    monitoringRowCount: 0,
    excludedIdentifiedAddressCount: 0,
    excludedExamples: [],
    rows: [],
    monitoringRows: [],
    sourceRows: [],
    summary: '',
    currentBasisHash: '',
    legendAnalysis
  };

  entries.forEach((entry) => {
    const entryHash = normalizeCaseDormantAddressHash(entry?.hash || '');
    (entry?.rows || []).forEach((entryRow) => {
      const row = Array.isArray(entryRow?.row) ? entryRow.row : [];
      if (!rowHasMeaningfulData(row)) return;
      const toAddress = normalizeCell(row[3]);
      const fundsFollowedRaw = normalizeCell(row[12]);
      if (!entryHash || !toAddress || !fundsFollowedRaw) return;
      const addressKey = normalizeCaseAddressLegendAddressKey(toAddress);
      if (!addressKey) return;
      const legend = legendLookup.get(addressKey) || null;
      const notesLookDormant = caseDormantNotesIndicateDormant(row[9]);
      const legendLooksDormant = caseDormantLegendIndicatesDormant(legend);
      if (!notesLookDormant && !legendLooksDormant) return;
      if (caseDormantLegendClearlyIdentifiesNonDormant(legend)) {
        state.excludedIdentifiedAddressCount += 1;
        addCaseDormantExcludedExample(state, legend?.label || toAddress);
        return;
      }
      const amountAndToken = buildCaseDormantAmountAndToken(row[5], row[6]);
      const fundsFollowed = formatCaseDormantFundsString(fundsFollowedRaw);
      const leftBoundsData = normalizeCell(row[17]);
      const leftBoundsValue = normalizeCell(row[18]);
      const occurrenceKey = [addressKey, entryHash, amountAndToken, fundsFollowed, leftBoundsData, leftBoundsValue].join('|');
      if (seenOccurrenceKeys.has(occurrenceKey)) return;
      seenOccurrenceKeys.add(occurrenceKey);
      uniqueTransactionHashes.add(entryHash);
      const occurrence = {
        key: `dormant-monitoring-${caseBuddyHashString(occurrenceKey)}`,
        address: toAddress,
        amountAndToken,
        fundsFollowed,
        relatedTransactionHash: entryHash,
        leftBoundsData,
        leftBoundsValue,
        sheetRowNumber: Number(entryRow.rowIndex) + 1,
        rowIndex: Number(entryRow.rowIndex),
        pathLabel: normalizeCell(row[0]) || normalizeCell(entry.rows?.[0]?.row?.[0]),
        notes: normalizeCell(row[9]),
        attribution: normalizeCell(row[10]),
        searchText: [toAddress, amountAndToken, fundsFollowed, entryHash, leftBoundsData, leftBoundsValue, Number(entryRow.rowIndex) + 1, row[9], row[10]]
          .map(value => normalizeCell(value).toLowerCase())
          .filter(Boolean)
          .join(' ')
      };
      occurrences.push(occurrence);
    });
  });

  occurrences.sort((left, right) => {
    const addressCompare = normalizeCell(left.address).localeCompare(normalizeCell(right.address), undefined, { numeric: true, sensitivity: 'base' });
    if (addressCompare !== 0) return addressCompare;
    if ((left.sheetRowNumber || 0) !== (right.sheetRowNumber || 0)) return (left.sheetRowNumber || 0) - (right.sheetRowNumber || 0);
    return normalizeCell(left.relatedTransactionHash).localeCompare(normalizeCell(right.relatedTransactionHash), undefined, { numeric: true, sensitivity: 'base' });
  });

  state.monitoringRows = occurrences;
  state.sourceRows = occurrences.slice();
  state.rows = buildCaseDormantAddressTableRowsFromOccurrences(occurrences);
  state.uniqueTransactionCount = uniqueTransactionHashes.size;
  state.officialRowCount = state.rows.length;
  state.monitoringRowCount = state.monitoringRows.length;
  state.summary = buildCaseDormantTableSummary(state);
  state.currentBasisHash = `dormant-address-deliverables-${caseBuddyHashString(JSON.stringify({
    legendBasisHash: legendAnalysis.currentBasisHash || '',
    legendReviewedBasisHash: legendAnalysis.confirmedReviewBasisHash || '',
    rows: state.rows.map(row => [row.normalizedAddress, row.address, row.fundsFollowedDisplay, row.uniqueTransactionCount, row.occurrenceCount]),
    monitoring: state.monitoringRows.map(row => [row.address, row.amountAndToken, row.fundsFollowed, row.relatedTransactionHash, row.leftBoundsData, row.leftBoundsValue, row.sheetRowNumber]),
    excludedIdentifiedAddressCount: state.excludedIdentifiedAddressCount
  }))}`;
  return state;
}

export function buildCaseDormantAddressTableDeliverableRows(state = {}) {
  const rows = [CASE_DORMANT_ADDRESS_TABLE_HEADERS.slice()];
  (Array.isArray(state.rows) ? state.rows : []).forEach((row) => {
    rows.push([
      normalizeCell(row.address),
      normalizeCell(row.fundsFollowedCell || row.fundsFollowedDisplay)
    ]);
  });
  return rows;
}

export function buildCaseDormantAddressMonitoringRows(state = {}) {
  const rows = [CASE_DORMANT_ADDRESS_MONITORING_HEADERS.slice()];
  (Array.isArray(state.monitoringRows) ? state.monitoringRows : []).forEach((row) => {
    rows.push([
      normalizeCell(row.address),
      normalizeCell(row.amountAndToken),
      normalizeCell(row.fundsFollowed),
      normalizeCell(row.relatedTransactionHash)
    ]);
  });
  return rows;
}

export function buildCaseDormantAddressAuditRows(state = {}, actionLabel = 'Dormant address deliverables review') {
  return [
    ['Case Buddy Dormant Address Deliverables Audit', '', '', '', '', '', '', '', '', 'Audit only. Dormant address deliverables are generated from Main Data plus the reviewed Address Legend; Main Data is not modified.'],
    ['Action', actionLabel, 'Clean Rows', state.officialRowCount || 0, 'Monitoring Rows', state.monitoringRowCount || 0, 'Unique Txns', state.uniqueTransactionCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Legend Current', state.legendBuildIsCurrent ? 'Yes' : 'No', 'Legend Reviewed', state.legendReviewIsCurrent ? 'Yes' : 'No', 'Identified Exclusions', state.excludedIdentifiedAddressCount || 0],
    [],
    ...buildCaseDormantAddressTableDeliverableRows(state),
    [],
    ['Monitoring View'],
    ...buildCaseDormantAddressMonitoringRows(state),
    [],
    ['Excluded Identified Address Examples'],
    ['Label Or Address'],
    ...(Array.isArray(state.excludedExamples) ? state.excludedExamples : []).map(value => [value])
  ];
}
